import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL, getHeaders } from '../../utils/api';
import { ApiEndpoints } from '../../utils/ApiEndpoints';
import { readSitePolicyError } from '../../utils/siteDomainConfiguration';

export default function DomainPolicyRecovery({ operation, token, onResolved }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const request = async (retry) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(true);
    setError('');
    setStatus(null);
    try {
      const endpoint = retry ? ApiEndpoints.retryDomainPolicyOperation : ApiEndpoints.domainPolicyOperation;
      const response = await fetch(`${API_BASE_URL}${endpoint(operation.organizationId, operation.operationId)}`, {
        method: retry ? 'POST' : 'GET', headers: getHeaders(token), signal: controller.signal
      });
      if (!response.ok || response.status === 202) throw await readSitePolicyError(response);
      const result = await response.json();
      if (result?.operationId !== operation.operationId || !result.siteCollectionId ||
        !['pending', 'retryable', 'completed'].includes(result.status) || typeof result.canRetry !== 'boolean') {
        throw new Error('The server returned an invalid recovery status. Your draft remains blocked and retained.');
      }
      if (controller.signal.aborted) return;
      setStatus(result);
      if (result.status === 'completed') {
        if (operation.isBlockingOperation === true) {
          onResolved(null);
          return;
        }
        if (operation.isBlockingOperation !== false) {
          throw new Error('Operation completed, but request ownership was not confirmed. Your draft remains retained; do not retarget or resubmit it until its outcome is known.');
        }
        const siteResponse = await fetch(`${API_BASE_URL}${ApiEndpoints.siteCollection(result.siteCollectionId)}`, {
          headers: getHeaders(token), signal: controller.signal
        });
        if (!siteResponse.ok) throw await readSitePolicyError(siteResponse);
        const site = await siteResponse.json();
        if (site?.id !== result.siteCollectionId || site.organizationId !== operation.organizationId) {
          throw new Error('Recovered site identity did not match the operation organization. Your draft is retained.');
        }
        if (!controller.signal.aborted) onResolved(site);
      }
    } catch (failure) {
      if (controller.signal.aborted) return;
      setError(failure.message || 'Recovery status is unavailable. Your draft is retained.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <section aria-label="Domain policy recovery" className="mt-6 rounded-lg border border-amber-300 bg-amber-50/50 p-5 text-card-foreground shadow-sm space-y-4">
      <h4 className="text-base font-semibold tracking-tight text-amber-950">Recover {operation.isBlockingOperation === true ? 'blocking' : 'original'} domain-policy operation</h4>
      <p className="font-mono text-xs text-amber-900/80 bg-amber-100/60 rounded px-2.5 py-1.5 border border-amber-200">Organization: {operation.organizationId}. Operation ID: {operation.operationId}.</p>
      <p className="text-xs text-muted-foreground leading-relaxed">This replays the server's stored change, not your current draft. It does not create another site.
        Current draft edits remain here for review after recovery.</p>
      {operation.isBlockingOperation === true && <p className="text-xs text-amber-800 font-medium">This operation blocked your request before it started.
        Completing the blocker does not save or retarget your draft. Submit the preserved draft after recovery.</p>}
      <p role="status" className="text-xs font-medium text-foreground">Operation status: {status?.status || 'not checked'}.</p>
      {status?.status === 'pending' && <p className="text-xs text-amber-700 italic">The server has not established a safe retry. Check status again after backend reconciliation; do not recreate the site.</p>}
      {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs font-medium text-destructive">{error}</p>}
      <div className="flex flex-wrap gap-2.5 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => request(false)}
          className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3.5 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Checking recovery...' : 'Check operation status'}
        </button>
        <button
          type="button"
          disabled={busy || status?.status !== 'retryable' || status?.canRetry !== true}
          onClick={() => request(true)}
          className="inline-flex items-center justify-center rounded-md bg-amber-600 px-3.5 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-amber-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Retry {operation.isBlockingOperation === true ? 'blocking' : 'original'} operation
        </button>
      </div>
    </section>
  );
}
